<script lang="ts">
  import { Plus, Minus } from "lucide-svelte"
  import Modal from "$app/components/Modal.svelte"
  import RoleInfo from "./RoleInfo.svelte"
  import { signupStore } from "$app/signupstore.svelte"
  import { appStore } from "$app/store.svelte"
  import { MAX_FREE_MEMBERS } from "$app/constants"
  import { toast } from "$app/utils"

  interface Props {
    role: "full_access" | "team_member" | "recognition_member"
    min: number
    max: number
    onchange?: (num: number) => void
  }

  let { role, min, max, onchange = () => {} }: Props = $props()

  if (min < signupStore.membersCount(role)) {
    min = signupStore.membersCount(role)
  }

  const idx = signupStore.member_roles.findIndex((m) => m.id === role)
  let currentRole = signupStore.member_roles[idx]

  let showModal = $state(false)

  function decreaseMembers() {
    if (currentRole.count > min) {
      currentRole.count--
      onchange(currentRole.count)
    }
  }

  function increaseMembers() {
    if (signupStore.selectedPlan.id === "free") {
      if (signupStore.signupCountTotal() >= MAX_FREE_MEMBERS) {
        toast(
          `You can only have up to ${MAX_FREE_MEMBERS} members on the Free plan`,
          "error"
        )
        return
      }
    }

    if (currentRole.count < max) {
      currentRole.count++
      onchange(currentRole.count)
    }
  }

  function memberTitle() {
    switch (role) {
      case "full_access":
        return "Full-Access Member"
      case "team_member":
        return "Team Member"
      case "recognition_member":
        return "Recognition Member"
    }
  }

  function getInputNumber(e: Event): number {
    let val = parseInt((e.target as HTMLInputElement).value)
    if (Number.isNaN(val)) {
      return val
    }
    if (val > max) val = max
    if (val < min) val = min

    return val
  }

  signupStore.setSignupDataToSubscription()
</script>

<div>
  <button
    class="text-blue-600 flex items-center gap-1"
    onclick={() => (showModal = true)}
    type="button"
  >
    {memberTitle()}
  </button>

  <p class="text-gray-700">
    <span class="text-xs hidden md:inline-block">Price:</span>
    <span class="text-sm font-bold text-black">
      ${signupStore.priceRole(role)} / {signupStore.billingInterval}
    </span>
  </p>
</div>

<div class="flex items-center justify-center">
  <div class="flex items-center justify-center md:gap-x-1">
    <button
      class="border border-gray-200 p-1 rounded-md"
      class:opacity-50={currentRole.count === min}
      type="button"
      disabled={currentRole.count === min}
      onclick={decreaseMembers}
    >
      <Minus class="w-6 h-6" />
    </button>
    <input
      class="rounded-md bg-white text-black px-2 py-1.5 text-base text-center outline -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:-outline-offset-2 focus:outline-indigo-600"
      type="number"
      {min}
      {max}
      value={currentRole.count}
      onblur={() => {
        if (Number.isNaN(currentRole.count)) currentRole.count = min
      }}
      oninput={(event) => {
        currentRole.count = getInputNumber(event)
        onchange(currentRole.count)
      }}
    />
    <button
      class="border border-gray-200 p-1 rounded-md"
      type="button"
      class:opacity-50={currentRole.count === max}
      disabled={currentRole.count === max}
      onclick={increaseMembers}
    >
      <Plus class="w-6 h-6" />
    </button>
  </div>
</div>

<div class="text-right font-medium label whitespace-nowrap text-black">
  {#if signupStore.selectedPlan.id === "standard"}
    {signupStore.priceRoleTotal(role).toFixed(2)} / {signupStore.billingInterval}
  {/if}
</div>

<Modal bind:open={showModal}>
  <RoleInfo {role} />
</Modal>

<style>
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
</style>
